
"use client";

import { useState, Suspense, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Chrome, UserPlus, LogIn, Sparkles, CheckCircle2, Rocket, Save, Eye, AlertTriangle, Trash2, Info } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from 'next/navigation';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MainFooter } from "@/components/layout/main-footer";
import { AICreationChat } from "@/components/ai/AICreationChat";
import { getTemplateSettings } from "@/lib/actions/template-actions";
import { createSite, checkSubdomainExistsServer as checkSubdomainExists } from "@/lib/actions/site-actions";
import { createFreeTrialSubscription, findAvailableSubscriptionForNewSite } from "@/lib/actions/subscription-actions";
import { initialSiteData } from "@/lib/initial-site-data";
import { type SiteData } from "@/lib/types";
import { Skeleton } from "@/components/ui/skeleton";

// Renderers
import { Template1Renderer } from '@/components/mini-site-templates/template1/Template1Renderer';
import { Template2Renderer } from '@/components/mini-site-templates/template2/Template2Renderer';
import { Template3Renderer } from '@/components/mini-site-templates/template3/Template3Renderer';
import { Template4Renderer } from '@/components/mini-site-templates/template4/Template4Renderer';

const renderers: any = {
    template1: Template1Renderer,
    template2: Template2Renderer,
    template3: Template3Renderer,
    template4: Template4Renderer,
};

const MAX_ANONYMOUS_GENERATIONS = 3;

function AuthPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const { currentUser, loading: authLoading, signInWithGoogle, login, signup, isAdmin } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isSavingSite, setIsSavingSite] = useState(false);
  const [isCheckingStorage, setIsCheckingStorage] = useState(true);
  
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'signup');
  const [templates, setTemplates] = useState<any[]>([]);
  const [pendingSiteData, setPendingSiteData] = useState<any>(null);
  const [showTemporaryPreview, setShowTemporaryPreview] = useState(false);
  const [generationCount, setGenerationCount] = useState(0);

  useEffect(() => {
    getTemplateSettings().then(settings => {
      const array = Object.keys(settings).map(k => ({ id: k, ...settings[k] }));
      setTemplates(array);
    });
  }, []);

  useEffect(() => {
    if (!currentUser) {
        const saved = localStorage.getItem('pending_site_data');
        const count = parseInt(localStorage.getItem('anon_gen_count') || '0', 10);
        setGenerationCount(count);
        
        if (saved) {
            try {
                setPendingSiteData(JSON.parse(saved));
            } catch (e) {
                console.error("Error parsing saved site data", e);
            }
        }
    }
    const timer = setTimeout(() => setIsCheckingStorage(false), 500);
    return () => clearTimeout(timer);
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && pendingSiteData && !isSavingSite) {
        handleRealSave(pendingSiteData.data, pendingSiteData.templateId);
    } else if (currentUser && !pendingSiteData && !showTemporaryPreview) {
        router.push(isAdmin ? '/admin/dashboard' : '/dashboard/home');
    }
  }, [currentUser, pendingSiteData, isAdmin, router, showTemporaryPreview, isSavingSite]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try { await login(loginEmail, loginPassword); } 
    catch (error: any) { 
        console.error("Erro de login:", error);
        toast({ title: "Erro de Login", description: "E-mail ou senha inválidos. Verifique seus dados e tente novamente.", variant: "destructive" }); 
    } 
    finally { setIsLoading(false); }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) { toast({ title: "Atenção", description: "Você precisa aceitar os Termos e Políticas.", variant: "destructive" }); return; }
    if (signupPassword !== signupConfirmPassword) { toast({ title: "Erro", description: "As senhas não coincidem.", variant: "destructive" }); return; }
    setIsLoading(true);
    try { 
        await signup(signupEmail, signupPassword, signupName); 
    } catch (error: any) { 
        console.error("Erro de cadastro:", error);
        if (error.code === 'auth/email-already-in-use') {
            toast({ 
                title: "E-mail já cadastrado", 
                description: "Este e-mail já possui uma conta. Tente acessar com sua senha ou clique em 'Esqueci minha senha'.", 
                variant: "destructive" 
            });
        } else {
            toast({ title: "Erro no Cadastro", description: error.message || "Ocorreu um erro ao criar sua conta.", variant: "destructive" }); 
        }
    } 
    finally { setIsLoading(false); }
  };

  const handleFinishSite = (data: any, templateId: string) => {
      const payload = { data, templateId };
      setPendingSiteData(payload);
      
      if (!currentUser) {
          const newCount = generationCount + 1;
          setGenerationCount(newCount);
          localStorage.setItem('anon_gen_count', newCount.toString());
          
          localStorage.setItem('pending_site_data', JSON.stringify(payload));
          setShowTemporaryPreview(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
          handleRealSave(data, templateId);
      }
  };

  const handleRealSave = async (data: any, templateId: string) => {
      if (!currentUser) return;
      setIsSavingSite(true);
      try {
          const subscription = await findAvailableSubscriptionForNewSite(currentUser.uid) || 
                               (await createFreeTrialSubscription(currentUser.uid)).subscription;
          
          if (!subscription) throw new Error("Não foi possível obter um plano.");

          let subdomain = (data.companyName || 'site').toLowerCase().replace(/[^a-z0-9]/g, '-');
          const exists = await checkSubdomainExists(subdomain);
          if (exists) subdomain += '-' + Math.random().toString(36).substring(2, 5);

          const finalData: Omit<SiteData, 'id' | 'createdAt' | 'updatedAt' | 'userId' | 'subscriptionId'> = {
              ...initialSiteData,
              ...data,
              templateId: templateId as any,
              subdomain,
              isPublished: true,
          };

          await createSite(currentUser.uid, subscription.id!, finalData);
          localStorage.removeItem('pending_site_data');
          localStorage.removeItem('anon_gen_count');
          
          toast({ title: "Site Salvo!", description: "Seu Super Site foi criado com sucesso." });
          router.push("/dashboard/sites");
      } catch (e: any) {
          toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
      } finally {
          setIsSavingSite(false);
      }
  }

  const handleBackToEditor = () => {
      setShowTemporaryPreview(false);
      setActiveTab('signup');
      setTimeout(() => { document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' }); }, 100);
  };

  const handleRestart = () => {
      if (generationCount >= MAX_ANONYMOUS_GENERATIONS) {
          toast({
              title: "Limite de Testes Atingido",
              description: "Você já criou vários sites de teste. Cadastre-se para salvar seu favorito ou para continuar criando.",
              variant: "destructive",
          });
          handleBackToEditor();
          return;
      }

      if(confirm("Isso apagará o site atual para começar um novo. Tem certeza?")) {
          localStorage.removeItem('pending_site_data');
          setPendingSiteData(null);
      }
  };

  if (authLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>;

  if (showTemporaryPreview && pendingSiteData) {
      const Renderer = renderers[pendingSiteData.templateId] || Template1Renderer;
      const aiData = pendingSiteData.data;
      
      const previewData: SiteData = {
          ...initialSiteData,
          ...aiData, 
          templateId: pendingSiteData.templateId,
          userId: 'temp',
      };

      return (
          <div className="min-h-screen bg-background">
              <div className="fixed top-0 left-0 right-0 z-[100] bg-primary text-primary-foreground shadow-lg">
                  <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                          <Rocket className="h-5 w-5 hidden sm:block" />
                          <span className="font-semibold text-sm sm:text-base leading-tight">Seu site ficou pronto! Cadastre-se para salvá-lo agora.<br /><small>Após o cadastro, você poderá personalizar textos, cores e imagens</small></span>
                      </div>
                      <Button variant="secondary" size="sm" onClick={handleBackToEditor} className="font-bold border-2 border-white shadow-md">
                          <Save className="mr-2 h-4 w-4" /> SALVAR MEU SITE GRÁTIS
                      </Button>
                  </div>
              </div>
              <div className="pt-16">
                  <Renderer data={previewData} />
              </div>
          </div>
      );
  }

  const isOverLimit = generationCount >= MAX_ANONYMOUS_GENERATIONS;

  const renderAssistantArea = () => {
      if (isCheckingStorage) {
          return (
              <Card className="h-[550px] flex items-center justify-center border-dashed border-primary/20 bg-primary/5">
                  <div className="text-center space-y-4">
                      <Loader2 className="h-10 w-10 animate-spin text-primary/40 mx-auto" />
                      <p className="text-sm text-muted-foreground animate-pulse font-headline">Carregando assistente...</p>
                  </div>
              </Card>
          );
      }

      if (pendingSiteData && !currentUser) {
          return (
            <Card className="border-primary/20 shadow-xl bg-white/80 backdrop-blur overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                <CardHeader className="bg-primary/5 border-b border-primary/10 pb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="bg-green-100 p-2 rounded-full">
                            <CheckCircle2 className="h-6 w-6 text-green-600" />
                        </div>
                        <CardTitle className="font-headline text-2xl text-primary">Você já tem um site criado!</CardTitle>
                    </div>
                    <CardDescription className="text-base">O assistente IA já gerou uma versão incrível para o seu negócio.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6 text-center">
                    <Button 
                        onClick={() => setShowTemporaryPreview(true)}
                        className="w-full h-14 text-xl font-bold bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all hover:scale-[1.02]"
                    >
                        <Eye className="mr-3 h-6 w-6" /> VISUALIZAR SITE CRIADO
                    </Button>
                    
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm text-left shadow-sm">
                        <p className="font-bold mb-2 flex items-center gap-2 text-base text-amber-800">
                            <AlertTriangle className="h-5 w-5" /> Salve já seu site para não perder!
                        </p>
                        <p className="leading-relaxed">
                            Cadastre-se para salvar seu site. Após o cadastro você poderá personalizar os textos, cores e imagens e compartilhar com seus amigos e clientes.
                        </p>
                    </div>
                    
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleRestart}
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/5"
                    >
                        <Trash2 className="mr-2 h-4 w-4" /> Apagar este e começar outro do zero
                    </Button>
                </CardContent>
            </Card>
          );
      }

      if (isOverLimit) {
          return (
            <Card className="border-destructive/30 shadow-xl bg-white overflow-hidden animate-in fade-in zoom-in duration-500">
                <CardHeader className="bg-destructive/5 border-b border-destructive/10">
                    <div className="flex items-center gap-3">
                        <div className="bg-destructive/10 p-2 rounded-full">
                            <Info className="h-6 w-6 text-destructive" />
                        </div>
                        <CardTitle className="font-headline text-2xl text-destructive">Limite de Testes Atingido</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                    <p className="text-lg text-gray-700">Você já explorou o potencial da nossa IA para criar sites incríveis!</p>
                    <p className="text-muted-foreground">Para garantir a melhor experiência para todos, agora pedimos que você <strong>crie sua conta gratuita</strong> para continuar criando ou para salvar um de seus projetos.</p>
                    <Button 
                        onClick={() => setActiveTab('signup')}
                        className="w-full h-12 text-lg font-bold"
                    >
                        CRIAR MINHA CONTA AGORA
                    </Button>
                </CardContent>
            </Card>
          );
      }

      return <AICreationChat templates={templates} onFinish={handleFinishSite} isSubmitting={isSavingSite} initialTemplateId="template1" />;
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <div className="flex-grow container mx-auto px-4 py-8 md:py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                    <Sparkles className="h-4 w-4" /> Tecnologia IA ativa
                </div>
                <h2 className="text-4xl md:text-5xl font-extrabold font-headline leading-tight">
                    Fale sobre sua empresa e nossa <span className="text-primary">IA</span> cria o site para você
                </h2>
                <p className="text-lg text-muted-foreground max-w-lg">Somente algumas perguntas e o site fica pronto para você personalizar.</p>
            </div>
            
            {renderAssistantArea()}

            <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-green-500" /> Grátis para começar</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-green-500" /> Publicação instantânea</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-green-500" /> Design responsivo</div>
            </div>
          </div>
          <div id="auth-section" className="flex flex-col items-center">
            <Card className="w-full max-w-md shadow-2xl border-primary/10">
              <CardHeader className="text-center space-y-4">
                <div className="flex justify-center"><div className="bg-primary p-3 rounded-2xl shadow-lg shadow-primary/20"><Rocket className="h-8 w-8 text-white" /></div></div>
                <div><CardTitle className="font-headline text-3xl">WebSite App</CardTitle><CardDescription>Acesse sua conta ou crie uma grátis</CardDescription></div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="login"><LogIn className="mr-2 h-4 w-4"/>Entrar</TabsTrigger>
                    <TabsTrigger value="signup"><UserPlus className="mr-2 h-4 w-4"/>Cadastrar</TabsTrigger>
                  </TabsList>
                  <TabsContent value="login">
                    <form onSubmit={handleLoginSubmit} className="space-y-4">
                      <div className="space-y-2"><Label htmlFor="login-email">Email</Label><Input id="login-email" type="email" placeholder="voce@exemplo.com" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} disabled={isLoading || isGoogleLoading}/></div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="login-password">Senha</Label>
                            <Link href="/forgot-password" size="sm" className="text-xs text-primary hover:underline">Esqueceu sua senha?</Link>
                        </div>
                        <Input id="login-password" type="password" placeholder="••••••••" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} disabled={isLoading || isGoogleLoading}/>
                      </div>
                      <Button className="w-full font-headline h-11" type="submit" disabled={isLoading || isGoogleLoading}>{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Entrar Agora</Button>
                      <div className="relative py-2"><div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Ou continue com</span></div></div>
                      <Button variant="outline" type="button" className="w-full h-11" onClick={() => signInWithGoogle()} disabled={isLoading || isGoogleLoading}><Chrome className="mr-2 h-4 w-4" /> Google</Button>
                    </form>
                  </TabsContent>
                  <TabsContent value="signup">
                    {pendingSiteData && (
                        <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-xl text-center animate-in fade-in zoom-in duration-700 shadow-sm">
                            <h3 className="text-lg font-extrabold text-primary flex items-center justify-center gap-2 leading-tight">
                                <Sparkles className="h-5 w-5 text-primary" />
                                CADASTRE-SE PARA SALVAR SEU SITE
                            </h3>
                            <p className="text-[10px] uppercase tracking-widest text-primary/70 font-bold mt-1">
                                Garanta que sua criação não seja perdida
                            </p>
                        </div>
                    )}
                    <form onSubmit={handleSignupSubmit} className="space-y-4">
                      <div className="space-y-2"><Label htmlFor="name">Nome Completo</Label><Input id="name" placeholder="Seu Nome" value={signupName} onChange={(e) => setSignupName(e.target.value)} disabled={isLoading || isGoogleLoading}/></div>
                      <div className="space-y-2"><Label htmlFor="signup-email">Email</Label><Input id="signup-email" type="email" placeholder="voce@exemplo.com" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} disabled={isLoading || isGoogleLoading}/></div>
                      <div className="space-y-2"><Label htmlFor="signup-password">Senha</Label><Input id="signup-password" type="password" placeholder="••••••••" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} disabled={isLoading || isGoogleLoading}/></div>
                      <div className="space-y-2"><Label htmlFor="confirm-password">Confirmar Senha</Label><Input id="confirm-password" type="password" placeholder="••••••••" value={signupConfirmPassword} onChange={(e) => setSignupConfirmPassword(e.target.value)} disabled={isLoading || isGoogleLoading}/></div>
                      <div className="flex items-start space-x-3 pt-2"><Checkbox id="terms" checked={agreedToTerms} onCheckedChange={(checked) => setAgreedToTerms(Boolean(checked))} disabled={isLoading || isGoogleLoading}/><div className="grid gap-1.5 leading-none"><label htmlFor="terms" className="text-xs text-muted-foreground">Concordo com os <Link href="/terms-of-service" className="text-primary underline">Termos</Link> e <Link href="/privacy-policy" className="text-primary underline">Privacidade</Link>.</label></div></div>
                      <Button className="w-full font-headline h-11" type="submit" disabled={isLoading || isGoogleLoading || !agreedToTerms}>{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Criar minha conta grátis</Button>
                      <div className="relative py-2"><div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Ou use o</span></div></div>
                      <Button variant="outline" type="button" className="w-full h-11" onClick={() => signInWithGoogle()} disabled={isLoading || isGoogleLoading || !agreedToTerms}><Chrome className="mr-2 h-4 w-4" /> Google</Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <MainFooter />
    </div>
  );
}

export default function HomeRootPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>}>
      <AuthPageContent />
    </Suspense>
  )
}
